import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useAuthStore from "@/shared/zustand/authStore";
import useProjectStore from "@/shared/zustand/projectManagementStore";
import useToastStore from "@/shared/zustand/toastStore";
import axios from "axios";
import { Check, FolderClosed, Share2, Trash, Users, X } from "lucide-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useRef, useState } from "react";
import ShareProjectDialog from "./ShareProjectDialog";
export const ProjectCardsSkeleton = () => {
    return (_jsx("div", { className: "py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr", style: {
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
        }, children: [1, 2, 3, 4, 5, 6, 7, 8].map((key) => (_jsxs("div", { className: "min-w-[350px] bg-[#fff] px-6 py-4 rounded-[8px] border border-[#D1D5DB] shadow-project-card flex flex-col gap-y-3 justify-between", children: [_jsx(Skeleton, { height: "24px", width: "70%", className: "mb-2" }), _jsx(Skeleton, { height: "14px", width: "50%" })] }, key))) }));
};
export const ProjectCard = ({ project, onSelect, isProjectSelection = false, onNameClick, isSelected, dashboardId, }) => {
    const { user } = useAuthStore();
    const showToast = useToastStore((state) => state.showToast);
    const removeProject = useProjectStore((state) => state.removeProject);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [shareProjectDialogVisible, setShareProjectDialogVisible] = useState(false);
    const [userAccessForThisProject, setUserAccessForThisProject] = useState(false);
    useEffect(() => {
        setUserAccessForThisProject(false); // Reset the state
        if (project && user && user.email) {
            if ("access_list" in project &&
                project.access_list &&
                Array.isArray(project.access_list) &&
                project.access_list.length > 0) {
                let hasAccess = false;
                project.access_list.forEach((access) => {
                    if (access.user_email === user.email &&
                        ["owner", "editor"].includes(access.role)) {
                        hasAccess = true;
                    }
                });
                setUserAccessForThisProject(hasAccess);
            }
        }
    }, [project]);
    // options states
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const formatDate = (timestamp) => {
        if (!timestamp)
            return "Not available";
        const [date] = timestamp.split(" "); // Extract only the date part
        const [day, month, year] = date.split(":").map(Number); // Parse the day, month, year
        const fullYear = year < 100 ? 2000 + year : year; // Handle two-digit year issue
        const formattedDate = new Intl.DateTimeFormat("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(Date.UTC(fullYear, month - 1, day)));
        return formattedDate;
    };
    const handleOpenShareProjectDialog = () => {
        setShareProjectDialogVisible(true);
    };
    const deleteProject = async () => {
        try {
            setIsDeleting(true);
            const bucket = new FormData();
            bucket.append("bucket_name", project.name);
            const response = await axios.delete(`${process.env.VITE_BASE_API_URL}/projects/${project._id}`);
            if (response.status === 200) {
                showToast("success", "Success", "Project deleted successfully.");
                // Update the store to remove the deleted project
                removeProject(project._id);
            }
        }
        catch (error) {
            console.error("Error deleting project:", error);
            showToast("error", "Error", "Failed to delete project. Check console for more details.");
        }
        finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    };
    const renderDialogFooter = () => {
        return (_jsxs("div", { className: "flex justify-end gap-2", children: [_jsxs(Button, { onClick: () => setShowDeleteDialog(false), className: "text-[#374151] text-sm ml-auto px-4 py-2 rounded-[6px] flex items-center gap-2 border border-[#D1D5DB]", disabled: isDeleting, children: [_jsx(X, { size: 16 }), _jsx("span", { children: "Cancel" })] }), _jsxs(Button, { onClick: deleteProject, className: "rounded-[6px] px-4 py-2 text-sm text-white flex items-center gap-x-2 transition-all duration-300 bg-red-500", loading: isDeleting, children: [_jsx(Trash, { size: 16, color: "white" }), _jsx("span", { children: "Delete" })] })] }));
    };
    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    // Toggle dropdown function
    const toggleDropdown = (e) => {
        e.stopPropagation();
        setShowDropdown(!showDropdown);
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { title: `Open ${project.name}`, className: "min-w-[350px] bg-[#fff] px-6 py-4 rounded-[8px] border border-[#D1D5DB] cursor-pointer shadow-project-card flex flex-col justify-evenly transform transition-transform duration-200 hover:-translate-y-1 relative", onClick: () => onSelect(project), role: "button", tabIndex: 0, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        onSelect(project);
                    }
                }, children: [_jsxs("div", { className: "absolute top-3 right-3 z-10", children: [_jsx("button", { ref: buttonRef, className: "p-2 rounded-full text-gray-600 hover:bg-gray-100", onClick: toggleDropdown, "aria-label": "More options", children: _jsx("i", { className: "pi pi-ellipsis-v" }) }), showDropdown && (_jsxs("div", { ref: dropdownRef, className: "absolute right-full top-3 bg-white border rounded shadow-md mt-0 mr-1 w-40 z-50", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: ` ${userAccessForThisProject
                                            ? "hover:bg-gray-100 cursor-pointer "
                                            : "text-gray-400 pointer-events-auto opacity-50 cursor-not-allowed"} p-2 flex items-center gap-2`, onClick: () => {
                                            if (!userAccessForThisProject)
                                                return;
                                            handleOpenShareProjectDialog();
                                            setShowDropdown(false);
                                        }, children: [_jsx(Share2, { size: 16 }), _jsx("span", { children: "Share" })] }), _jsxs("div", { className: ` ${userAccessForThisProject
                                            ? "hover:bg-gray-100 cursor-pointer "
                                            : "text-gray-400 pointer-events-auto opacity-50 cursor-not-allowed"} p-2 flex items-center gap-2`, onClick: () => {
                                            if (!userAccessForThisProject)
                                                return;
                                            setShowDeleteDialog(true);
                                            setShowDropdown(false);
                                        }, children: [_jsx(Trash, { size: 16 }), _jsx("span", { children: "Delete" })] })] }))] }), isProjectSelection && (_jsx("div", { className: `absolute right-12 top-3 w-6 h-6 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${isSelected ? "border-[#5928ed] bg-[#5928ed]" : "border-gray-400"}`, children: _jsx(Check, { size: 16, className: isSelected ? "text-white" : "text-gray-400" }) })), _jsxs("div", { className: "w-full overflow-x-hidden space-y-2", children: [_jsxs("div", { className: "flex gap-x-3 items-center", children: [_jsx(FolderClosed, { size: 20, className: "" }), user?.email &&
                                        project?.user &&
                                        project.user.length > 0 &&
                                        user.email !== project.user[0] &&
                                        (project.created_by ? (_jsxs("span", { className: "text-xs 2xl:text-sm text-[#9CA3AF] flex gap-x-1 items-center text-start", children: [" ", _jsx(Users, { size: 14 }), " Created by ", project.created_by] })) : null)] }), isProjectSelection ? (_jsx("h3", { className: "project-title w-fit text-base 2xl:text-lg font-semibold text-[#101828] mb-1 leading-5 font-heading", onClick: (e) => {
                                    e.stopPropagation();
                                    if (onNameClick)
                                        onNameClick(project);
                                }, "data-pr-tooltip": project.name, children: project.name.length > 50
                                    ? project.name.slice(0, 50) + "..."
                                    : project.name })) : (_jsx("h3", { className: "project-title w-fit text-base 2xl:text-lg font-semibold text-[#101828] mb-1 leading-5 font-heading", "data-pr-tooltip": project.name, children: project.name.length > 50
                                    ? project.name.slice(0, 50) + "..."
                                    : project.name }))] }), _jsx("div", { children: _jsx("span", { className: "text-xs 2xl:text-sm text-[#9CA3AF]", children: project.timestamp
                                ? formatDate(project.timestamp)
                                : "Recently modified" }) })] }), _jsx(Dialog, { header: "Confirm Deletion", visible: showDeleteDialog, style: { width: "450px" }, modal: true, draggable: false, onHide: () => setShowDeleteDialog(false), footer: renderDialogFooter, blockScroll: true, appendTo: dashboardId?.current, className: "max-w-[600px] font-content rounded-[8px] bg-[#fff] shadow-none border", maskClassName: "!absolute !rounded-[8px] backdrop-blur-sm !bg-transparent border border-[#d1d5db]", children: _jsxs("div", { className: "flex flex-col gap-3 p-3", children: [_jsxs("p", { className: "font-medium text-gray-700", children: ["Are you sure you want to delete the project", " ", _jsxs("span", { className: "font-bold text-gray-900", children: ["\"", project.name, "\""] }), "?"] }), _jsx("p", { className: "text-sm text-gray-600", children: "This action cannot be undone. All associated data will be permanently removed." })] }) }), shareProjectDialogVisible && project && (_jsx(ShareProjectDialog, { visible: shareProjectDialogVisible, onHide: () => setShareProjectDialogVisible(false), selectedProjectToShare: project, parentRef: dashboardId }))] }));
};
